import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  getMySubscription,
  getAvailablePlans,
  redirectToCheckout,
  redirectToPortal,
  cancelSubscription,
} from '../../services/subscriptionService';
import {
  SubscriptionResponse,
  SubscriptionPlan,
  hasSubscription,
  formatPrice,
  getDaysRemaining,
} from '../../types/subscription';
import { useConfirmModal } from '../../hooks/useConfirmModal';
import ConfirmModal from '../common/ConfirmModal';

interface FieldSubscriptionSectionProps {
  $night?: boolean;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const CurrentSubscriptionCard = styled.div<{ $night?: boolean; $status?: string }>`
  background: ${({ $status }) =>
    $status === 'active'
      ? 'linear-gradient(135deg, rgba(0, 255, 136, 0.1), rgba(0, 200, 100, 0.05))'
      : $status === 'trial'
      ? 'linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(0, 150, 200, 0.05))'
      : 'rgba(5, 7, 10, 0.85)'};
  border: 1px solid ${({ $status }) =>
    $status === 'active'
      ? 'rgba(0, 255, 136, 0.3)'
      : $status === 'trial'
      ? 'rgba(0, 212, 255, 0.3)'
      : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  padding: 24px;
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  ${({ $status }) => {
    switch ($status) {
      case 'active':
        return `
          background: linear-gradient(135deg, #00ff88, #00cc66);
          color: white;
        `;
      case 'trial':
        return `
          background: linear-gradient(135deg, #00d4ff, #0099cc);
          color: white;
        `;
      case 'canceled':
      case 'expired':
        return `
          background: linear-gradient(135deg, #ff4444, #cc3333);
          color: white;
        `;
      default:
        return `
          background: rgba(100, 100, 100, 0.5);
          color: #ccc;
        `;
    }
  }}
`;

const SubscriptionTitle = styled.h3`
  color: #00d4ff;
  margin: 0 0 16px 0;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SubscriptionInfo = styled.div`
  display: grid;
  gap: 12px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 700;
`;

const InfoValue = styled.span`
  color: #ffffff;
  font-weight: 700;
  text-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  ${({ $variant }) => {
    switch ($variant) {
      case 'danger':
        return `
          background: transparent;
          border: 1px solid rgba(255, 68, 68, 0.5);
          color: #ff4444;
          &:hover {
            background: rgba(255, 68, 68, 0.1);
            border-color: #ff4444;
          }
        `;
      case 'secondary':
        return `
          background: transparent;
          border: 1px solid rgba(0, 212, 255, 0.5);
          color: #00d4ff;
          &:hover {
            background: rgba(0, 212, 255, 0.1);
            border-color: #00d4ff;
          }
        `;
      default:
        return `
          background: linear-gradient(135deg, #00d4ff, #0099cc);
          border: none;
          color: white;
          &:hover {
            background: linear-gradient(135deg, #00e5ff, #00aadd);
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
`;

const PlanCard = styled.div<{ $recommended?: boolean }>`
  background: rgba(5, 7, 10, 0.85);
  border: 1px solid ${({ $recommended }) =>
    $recommended ? 'rgba(0, 212, 255, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 12px;
  padding: 24px;
  position: relative;
  transition: all 0.3s ease;

  ${({ $recommended }) =>
    $recommended &&
    `
    box-shadow: 0 0 20px rgba(0, 212, 255, 0.2);
  `}

  &:hover {
    border-color: rgba(0, 212, 255, 0.5);
    transform: translateY(-4px);
  }
`;

const RecommendedBadge = styled.div`
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: bold;
  text-transform: uppercase;
`;

const PlanName = styled.h4`
  color: #00d4ff;
  margin: 0 0 8px 0;
  font-size: 1.2rem;
`;

const PlanDescription = styled.p`
  color: #ffffff;
  margin: 0 0 16px 0;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.4;
`;

const PlanPricing = styled.div`
  margin-bottom: 16px;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
`;

const PriceAmount = styled.span`
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 700;
  text-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
`;

const PriceInterval = styled.span`
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 700;
`;

const PriceSavings = styled.span`
  color: #00ff88;
  font-size: 0.8rem;
`;

const FeaturesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 20px 0;
`;

const FeatureItem = styled.li`
  color: #ffffff;
  padding: 6px 0;
  font-size: 0.85rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '✓';
    color: #00ff88;
    font-weight: bold;
  }
`;

const NoSubscriptionMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #888;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;

  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 3px solid rgba(0, 212, 255, 0.3);
    border-top-color: #00d4ff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const BillingToggle = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${({ $active }) => ($active ? 'rgba(0, 212, 255, 0.2)' : 'transparent')};
  border: 1px solid ${({ $active }) => ($active ? '#00d4ff' : 'rgba(255, 255, 255, 0.2)')};
  color: ${({ $active }) => ($active ? '#00d4ff' : '#888')};

  &:hover {
    border-color: #00d4ff;
    color: #00d4ff;
  }
`;

const TrialBanner = styled.div`
  background: linear-gradient(135deg, rgba(255, 170, 0, 0.15), rgba(255, 136, 0, 0.05));
  border: 1px solid rgba(255, 170, 0, 0.3);
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const TrialIcon = styled.span`
  font-size: 1.5rem;
`;

const TrialText = styled.div`
  color: #ffaa00;
  font-size: 0.95rem;

  strong {
    display: block;
    margin-bottom: 4px;
  }
`;

const FieldSubscriptionSection: React.FC<FieldSubscriptionSectionProps> = ({ $night }) => {
  const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [billingInterval, setBillingInterval] = useState<'monthly' | 'yearly'>('monthly');
  const { confirm, modalProps } = useConfirmModal();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [subData, plansData] = await Promise.all([
        getMySubscription(),
        getAvailablePlans(),
      ]);
      setSubscription(subData);
      setPlans(plansData);
    } catch (error) {
      console.error('Failed to load subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId: string) => {
    try {
      setActionLoading(true);
      await redirectToCheckout(planId, billingInterval);
    } catch (error) {
      console.error('Failed to start checkout:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setActionLoading(true);
      await redirectToPortal(window.location.href);
    } catch (error) {
      console.error('Failed to open portal:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    const confirmed = await confirm({
      title: 'Cancel Subscription',
      message: 'Are you sure you want to cancel your subscription? It will remain active until the end of the current billing period and will not auto-renew.',
      confirmText: 'Yes, Cancel',
      cancelText: 'Keep Subscription',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      setActionLoading(true);
      await cancelSubscription({ atPeriodEnd: true });
      await loadData();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const hasSub = subscription && hasSubscription(subscription);
  const isTrialing = hasSub && subscription.status === 'trial';
  const isActive = hasSub && subscription.status === 'active';
  const isCanceled = hasSub && (subscription.status === 'canceled' || subscription.cancelAtPeriodEnd);

  return (
    <Container>
      {/* Current Subscription Status */}
      {hasSub ? (
        <CurrentSubscriptionCard $status={subscription.status}>
          <SubscriptionTitle>
            {subscription.planName}
            <StatusBadge $status={subscription.status}>
              {subscription.cancelAtPeriodEnd ? 'Canceling' : subscription.status}
            </StatusBadge>
          </SubscriptionTitle>

          {isTrialing && subscription.trialEnd && (
            <TrialBanner>
              <TrialIcon>⏰</TrialIcon>
              <TrialText>
                <strong>Trial Period</strong>
                {getDaysRemaining(subscription.trialEnd)} days remaining. Subscribe to continue using all features.
              </TrialText>
            </TrialBanner>
          )}

          <SubscriptionInfo>
            <InfoRow>
              <InfoLabel>Plan</InfoLabel>
              <InfoValue>{subscription.planName}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Billing</InfoLabel>
              <InfoValue>{subscription.billingInterval === 'yearly' ? 'Yearly' : 'Monthly'}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>Current Period</InfoLabel>
              <InfoValue>
                {new Date(subscription.currentPeriodStart).toLocaleDateString()} -{' '}
                {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
              </InfoValue>
            </InfoRow>
            {subscription.cancelAtPeriodEnd && (
              <InfoRow>
                <InfoLabel>Access Until</InfoLabel>
                <InfoValue style={{ color: '#ffaa00' }}>
                  {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                </InfoValue>
              </InfoRow>
            )}
            <InfoRow>
              <InfoLabel>Active Features</InfoLabel>
              <InfoValue>{subscription.activeFeatures?.length || 0} features</InfoValue>
            </InfoRow>
          </SubscriptionInfo>

          <ButtonsRow>
            {subscription.stripeSubscriptionId && (
              <ActionButton
                $variant="secondary"
                onClick={handleManageSubscription}
                disabled={actionLoading}
              >
                Manage Billing
              </ActionButton>
            )}
            {isActive && !subscription.cancelAtPeriodEnd && (
              <ActionButton
                $variant="danger"
                onClick={handleCancelSubscription}
                disabled={actionLoading}
              >
                Cancel Subscription
              </ActionButton>
            )}
          </ButtonsRow>
        </CurrentSubscriptionCard>
      ) : (
        <NoSubscriptionMessage>
          <h3 style={{ color: '#00d4ff', marginBottom: '12px' }}>No Active Subscription</h3>
          <p>Choose a plan below to unlock premium features for your field.</p>
        </NoSubscriptionMessage>
      )}

      {/* Available Plans */}
      {(!hasSub || isTrialing || isCanceled) && plans.length > 0 && (
        <>
          <h3 style={{ color: '#00d4ff', margin: '20px 0 16px' }}>Available Plans</h3>

          <BillingToggle>
            <ToggleButton
              $active={billingInterval === 'monthly'}
              onClick={() => setBillingInterval('monthly')}
            >
              Monthly
            </ToggleButton>
            <ToggleButton
              $active={billingInterval === 'yearly'}
              onClick={() => setBillingInterval('yearly')}
            >
              Yearly (Save 20%)
            </ToggleButton>
          </BillingToggle>

          <PlansGrid>
            {plans.map((plan, index) => (
              <PlanCard key={plan.id} $recommended={index === 1}>
                {index === 1 && <RecommendedBadge>Recommended</RecommendedBadge>}
                <PlanName>{plan.displayName}</PlanName>
                <PlanDescription>{plan.description}</PlanDescription>

                <PlanPricing>
                  <PriceRow>
                    <PriceAmount>
                      {formatPrice(
                        billingInterval === 'yearly' ? plan.priceYearly / 12 : plan.priceMonthly,
                        plan.currency
                      )}
                    </PriceAmount>
                    <PriceInterval>/month</PriceInterval>
                  </PriceRow>
                  {billingInterval === 'yearly' && (
                    <PriceSavings>
                      Billed {formatPrice(plan.priceYearly, plan.currency)}/year
                    </PriceSavings>
                  )}
                </PlanPricing>

                <FeaturesList>
                  {plan.features.map((feature, idx) => (
                    <FeatureItem key={idx}>{feature}</FeatureItem>
                  ))}
                </FeaturesList>

                <ActionButton
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={actionLoading}
                  style={{ width: '100%' }}
                >
                  {plan.trialDays > 0 ? `Start ${plan.trialDays}-Day Free Trial` : 'Subscribe Now'}
                </ActionButton>
              </PlanCard>
            ))}
          </PlansGrid>
        </>
      )}

      <ConfirmModal {...modalProps} />
    </Container>
  );
};

export default FieldSubscriptionSection;
